import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import apiServices from '../../services/service';
import { useDispatch, useSelector } from 'react-redux';
import {
  getTicketList,
  getGridDataByModuleKey,
  getGenericMasterFilteredListByKey,
  getActiveOwners,
} from '../../redux/selector';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import { fetchDataAndUpdateStore, fetchSelectedListAndAppendToModule } from '../../redux/actions';
import { ticketInputData } from '../../shared/constants';
import GlobalSearch from '../sharedChild/grid/globalSearch';
import FilterHandler from '../sharedChild/grid/filterHandler';
import useTaskColumnDefs from './useTaskColumnDefs';
import { updateGridColumnState } from '../../redux/reducer';
import gridOptionsConfig from '../sharedChild/grid/gridOptionsConfig';
import useFetchIfNeeded from '../../redux/useFetchIfNeeded';
import GridSkeleton from '../../shared/component/GridSkeleton';
import LeadsColumns from '../leads/LeadsColumns';
import LeadsFilters, {
  buildAgFilterModel,
  defaultCond,
  condActive,
  ActiveFilterBar,
} from '../leads/LeadsFilters';

function Tasks() {
  const stateData = useSelector((state) => state);
  const [gridParams, setGridParams] = useState(null);
  const dispatch = useDispatch();
  let storeLoading = isLoadingByKey(stateData, 'ticketList', 'owners');

  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const gridRef = useRef();
  const rowData = useSelector(getTicketList);
  const gridData = useSelector(getGridDataByModuleKey('ticketList'));
  const navigate = useNavigate();
  const location = useLocation();
  const owner = useSelector(getActiveOwners);

  const [loading, setLoading] = useState(storeLoading);
  const ticketPriorityDropdown = useSelector(getGenericMasterFilteredListByKey('TicketPriorityCrm'));
  const ticketStatusDropdown = useSelector(getGenericMasterFilteredListByKey('StatusCrm'));
  const gridDataSelection = useSelector(getGridDataByModuleKey('selectionList'));
  const gridOptions = gridOptionsConfig('ticketList', navigate);

  useFetchIfNeeded(getTicketList, fetchDataAndUpdateStore, {
    key: 'ticketList',
    inputData: ticketInputData,
  });

  let hasCreateTicketPermission = true;
  let hasDeleteTicketPermission = true;
  let userUIControlPermission = JSON.parse(localStorage.getItem('userUIControlPermission'))?.ticketmenu;
  if (userUIControlPermission) {
    hasCreateTicketPermission = userUIControlPermission?.ticketaddscreen
      ? userUIControlPermission.ticketaddscreen.canView
      : true;
    hasDeleteTicketPermission = userUIControlPermission?.ticketdeletescreen
      ? userUIControlPermission.ticketdeletescreen.canView
      : true;
  }

  // GrowthOS re-skin: custom Filters + Columns panels (reused from leads)
  const [filterConds, setFilterConds] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [expandedFilter, setExpandedFilter] = useState(null);
  const [colsOpen, setColsOpen] = useState(false);
  const [hiddenCols, setHiddenCols] = useState(() => new Set());

  const fullSelection = Object.values(gridDataSelection?.ticketList || {}).flat();

  const handleClickDelete = (paramsValue) => {
    setLoading(true);
    let data;
    if (paramsValue?.id === undefined) {
      const selectedIds = fullSelection.map((row) => row.id);
      data = { id: selectedIds };
    } else {
      data = { id: [paramsValue.id] };
    }
    apiServices
      .deleteTicket(data)
      .then((response) => {
        if (response.status === 200) {
          setLoading(false);
          if (paramsValue?.id === undefined) {
            document.getElementById('massDeleteModal').click();
          }
          dispatch(fetchSelectedListAndAppendToModule({ key: 'ticketList', selectedList: {} }));
          dispatch(
            fetchDataAndUpdateStore({
              key: 'ticketList',
              inputData: gridData?.inputPayload ? gridData?.inputPayload : ticketInputData,
            })
          );
          toast.success('Ticket Deleted Successfully!', { position: 'top-right' });
        } else {
          setLoading(false);
          toast.error('Error deleting record', { position: 'top-right' });
        }
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
        setLoading(false);
      });
  };

  const columnDefs = useTaskColumnDefs({
    ticketStatusDropdown,
    ticketPriorityDropdown,
    owner,
    gridData,
  });

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      editable: false,
      flex: 1,
      minWidth: 130,
      filter: true,
      resizable: true,
      enableValue: true,
      enableRowGroup: true,
      enablePivot: true,
    }),
    []
  );

  const popupParent = useMemo(() => document.body, []);
  const sortingOrder = useMemo(() => ['desc', 'asc', null], []);

  // ---- Filters (drive AG filterModel = server-side via handleFilterChanged) ----
  const filterFields = useMemo(
    () => [
      { field: 'status', label: 'Status', type: 'set', vals: (ticketStatusDropdown || []).map((x) => x.displayName).filter(Boolean) },
      { field: 'priority', label: 'Priority', type: 'set', vals: (ticketPriorityDropdown || []).map((x) => x.displayName).filter(Boolean) },
      { field: 'owner', label: 'Owner', type: 'set', vals: (owner || []).map((x) => x.display_name).filter(Boolean) },
      { field: 'subject', label: 'Subject', type: 'text' },
      { field: 'leadName', label: 'Lead Name', type: 'text' },
      { field: 'contactName', label: 'Contact Name', type: 'text' },
      { field: 'accountName', label: 'Account Name', type: 'text' },
      { field: 'opportunityName', label: 'Opportunity Name', type: 'text' },
      { field: 'campaignName', label: 'Campaign Name', type: 'text' },
      { field: 'dueDate', label: 'Due Date', type: 'date' },
      { field: 'createdDateTime', label: 'Created Date', type: 'date' },
    ],
    [ticketStatusDropdown, ticketPriorityDropdown, owner]
  );
  const applyFilterConds = useCallback(
    (c) => {
      const api = gridRef.current?.api;
      if (api) api.setFilterModel(buildAgFilterModel(c, filterFields));
    },
    [filterFields]
  );
  const setFilterCond = (field, patch) => {
    setFilterConds((prev) => {
      const meta = filterFields.find((x) => x.field === field);
      const base = prev[field] || defaultCond(meta.type);
      return { ...prev, [field]: { ...base, type: meta.type, ...patch } };
    });
  };
  const removeFilterCond = (field) => {
    setFilterConds((prev) => {
      const n = { ...prev };
      delete n[field];
      applyFilterConds(n);
      return n;
    });
    setExpandedFilter((e) => (e === field ? null : e));
  };
  const clearAllFilters = () => {
    setFilterConds({});
    applyFilterConds({});
    setExpandedFilter(null);
  };
  const hasActiveFilters = Object.values(filterConds).some(condActive);

  // ---- Columns show/hide ----
  const columnMenuList = useMemo(
    () =>
      columnDefs
        .filter((c) => c.field && c.headerName)
        .map((c) => ({ id: c.field, label: c.headerName, locked: c.field === 'subject' })),
    [columnDefs]
  );
  const syncHiddenCols = useCallback(() => {
    const colApi = gridRef.current?.columnApi;
    if (!colApi?.getColumnState) return;
    setHiddenCols(new Set(colApi.getColumnState().filter((c) => c.hide).map((c) => c.colId)));
  }, []);
  const toggleCol = (id) => {
    const colApi = gridRef.current?.columnApi;
    const api = gridRef.current?.api;
    const makeVisible = hiddenCols.has(id);
    if (colApi?.setColumnsVisible) colApi.setColumnsVisible([id], makeVisible);
    else if (colApi?.setColumnVisible) colApi.setColumnVisible(id, makeVisible);
    else if (api?.setColumnsVisible) api.setColumnsVisible([id], makeVisible);
    syncHiddenCols();
  };
  const toggleAllCols = () => {
    const colApi = gridRef.current?.columnApi;
    const api = gridRef.current?.api;
    const toggleable = columnMenuList.filter((c) => !c.locked).map((c) => c.id);
    if (toggleable.length === 0) return;
    const allShown = toggleable.every((id) => !hiddenCols.has(id));
    const makeVisible = !allShown;
    if (colApi?.setColumnsVisible) colApi.setColumnsVisible(toggleable, makeVisible);
    else if (api?.setColumnsVisible) api.setColumnsVisible(toggleable, makeVisible);
    syncHiddenCols();
  };

  const handleColumnChanged = () => {
    const columnApi = gridRef.current?.columnApi;
    if (!columnApi) return;
    dispatch(updateGridColumnState({ moduleKey: 'ticketList', columnState: columnApi.getColumnState() }));
    syncHiddenCols();
  };

  const handleGridReady = (params) => {
    gridRef.current = params;
    setGridParams(params);
    const columnOrderFromStore = gridData?.columnState;
    if (columnOrderFromStore) {
      params.columnApi.applyColumnState({ state: columnOrderFromStore, applyOrder: true });
    } else {
      const fieldsToHide = ['createdDateTime', 'updatedDateTime'];
      const defaultState = params.columnApi.getColumnState().map((col) => ({
        ...col,
        hide: fieldsToHide.includes(col.colId),
      }));
      params.columnApi.applyColumnState({ state: defaultState, applyOrder: true });
    }
    syncHiddenCols();
  };

  const handleOpenAddForm = () => {
    navigate('/tickets/addTicket');
  };

  const onSelectionChanged = () => {
    const gridApi = gridRef.current.api;
    const selectedRowsLocal = gridApi.getSelectedRows();
    const key = 'page-' + gridData?.inputPayload?.pageNumber;
    dispatch(
      fetchSelectedListAndAppendToModule({
        key: 'ticketList',
        selectedList: { [key]: selectedRowsLocal },
      })
    );
  };

  const clearTicketSelection = () => {
    gridRef.current?.api?.deselectAll();
    dispatch(fetchSelectedListAndAppendToModule({ key: 'ticketList', selectedList: {} }));
  };

  const onFirstDataRendered = useCallback(() => {
    gridRef.current?.api?.paginationGoToPage();
    syncHiddenCols();
  }, [syncHiddenCols]);

  const filterDefaultSetRef = useRef(null);
  const handleFilterChanged = useCallback(
    (event) => {
      if (filterDefaultSetRef.current) {
        filterDefaultSetRef.current = false;
        return;
      }
      const filterModel = event.api.getFilterModel();
      const payload = {
        ...gridData?.inputPayload,
        filterJson: JSON.stringify(filterModel),
        pageNumber: 1,
      };
      dispatch(fetchDataAndUpdateStore({ key: 'ticketList', inputData: payload }));
    },
    [dispatch, gridData?.inputPayload]
  );

  const handleSortChanged = useCallback(
    (event) => {
      const sortModel = event.columnApi
        .getAllDisplayedColumns()
        .map((col) => ({ colId: col.getColId(), sort: col.getSort() }))
        .filter((col) => col.sort);
      const columnName = sortModel.length > 0 ? sortModel[0].colId : 'UpdatedDateTime';
      const orderType = sortModel.length > 0 ? sortModel[0].sort : 'desc';
      const payload = { ...gridData?.inputPayload, columnName, orderType, pageNumber: 1 };
      dispatch(fetchDataAndUpdateStore({ key: 'ticketList', inputData: payload }));
    },
    [dispatch, gridData?.inputPayload]
  );

  const exportAll = async () => {
    setLoading(true);
    const fetchData = {
      ...ticketInputData,
      pageSize: gridData?.totalCount,
      searchText: gridData?.inputPayload?.searchText ? gridData?.inputPayload?.searchText : null,
      filterJson: gridData?.inputPayload?.filterJson ? gridData?.inputPayload?.filterJson : null,
    };
    try {
      const response = await apiServices.exportAllTicket(fetchData);
      if (response?.data && response.status === 200) {
        setLoading(false);
        if (response?.data.includes('Export initiated.')) {
          toast.warning(response?.data);
          setLoading(false);
        } else {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'Tickets.csv');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } else {
        console.error('No data received from API.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error exporting Tickets:', error);
      setLoading(false);
    }
  };

  // Restore selection on rowData change
  useEffect(() => {
    const gridApi = gridParams?.api;
    if (!gridApi || !gridData?.inputPayload?.pageNumber) return;
    const pageKey = 'page-' + gridData.inputPayload.pageNumber;
    const selectedRowsDefault = gridDataSelection?.ticketList?.[pageKey] || [];
    if (selectedRowsDefault.length > 0) {
      gridApi.forEachNode((node) => {
        const isSelected = selectedRowsDefault.some((row) => row.id === node.data?.id);
        node.setSelected(isSelected);
      });
    } else {
      gridApi.deselectAll();
    }
  }, [gridParams, rowData, location.pathname]);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  return (
    <div className={`inner-page gos-leads gos-tickets${hasActiveFilters ? ' has-filterbar' : ''}`} style={containerStyle}>
      <div className="dash-container">
        <div className="tbl_container">
          <div className="row">
            <div className="col-12 col-sm-12 col-md-12">
              {/* GrowthOS header */}
              <div className="main-hd">
                <div className="gos-lead-head">
                  <div className="pagetitle">Tickets</div>
                  {gridData?.totalCount !== undefined && (
                    <div className="gos-page-sub">{Number(gridData.totalCount).toLocaleString()} tickets</div>
                  )}
                </div>
                <div className="third-div gos-lead-actions">
                  <button type="button" className="gos-btn gos-btn-ghost" onClick={exportAll} disabled={rowData && rowData.length === 0}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></svg>
                    Export
                  </button>
                  {hasCreateTicketPermission && (
                    <button type="button" className="gos-btn gos-btn-primary" onClick={handleOpenAddForm}>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                      New Ticket
                    </button>
                  )}
                </div>
              </div>

              {/* toolbar: bulk-bar when rows selected, else search + filters/columns */}
              {fullSelection.length > 0 ? (
                <div className="gos-lead-toolbar bulk-bar">
                  <div className="bulk-left">
                    <button type="button" className="bulk-clear" onClick={clearTicketSelection} title="Clear selection">
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                    </button>
                    <span className="bulk-count">{fullSelection.length} selected</span>
                  </div>
                  <div className="bulk-right">
                    {hasDeleteTicketPermission && (
                      <button type="button" className="gos-btn gos-btn-ghost gos-btn-sm danger" data-bs-toggle="modal" data-bs-target="#delete">
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6h12M10 11v6M14 11v6" /></svg>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="gos-lead-toolbar">
                  <div className="gos-lead-toolbar-left">
                    <div className="gos-lead-toolbar-search">
                      <GlobalSearch moduleName={'ticketList'} fullSelection={fullSelection} placeholder="Search tickets…" />
                    </div>
                    {gridData?.totalCount !== undefined && (
                      <span className="result-count">
                        <strong>{Number(gridData.totalCount).toLocaleString()}</strong>{' '}
                        {gridData?.inputPayload?.searchText ? 'matches' : 'tickets'}
                      </span>
                    )}
                  </div>
                  <div className="gos-lead-toolbar-right">
                    <LeadsFilters
                      fields={filterFields}
                      conds={filterConds}
                      setCond={setFilterCond}
                      removeCond={removeFilterCond}
                      clearAll={clearAllFilters}
                      onApply={applyFilterConds}
                      expanded={expandedFilter}
                      setExpanded={setExpandedFilter}
                      open={filterOpen}
                      setOpen={setFilterOpen}
                    />
                    <LeadsColumns
                      columns={columnMenuList}
                      hidden={hiddenCols}
                      onToggle={toggleCol}
                      onToggleAll={toggleAllCols}
                      open={colsOpen}
                      setOpen={setColsOpen}
                    />
                  </div>
                </div>
              )}
              {fullSelection.length === 0 && (
                <ActiveFilterBar
                  fields={filterFields}
                  conds={filterConds}
                  onEdit={(f) => {
                    setExpandedFilter(f);
                    setFilterOpen(true);
                  }}
                  onRemove={removeFilterCond}
                  onClear={clearAllFilters}
                />
              )}

              <div className="cutome_ag_theme">
                {loading && <GridSkeleton rows={10} />}
                <div className={`ag-theme-balham tbl_height`} style={{ width: '100%', height: `100%` }}>
                  <AgGridReact
                    className="ag-grid-container"
                    domLayout="fixedHeight"
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    overlayLoadingTemplate={`<Loader />`}
                    animateRows={true}
                    rowSelection={'multiple'}
                    rowHeight={64}
                    headerHeight={44}
                    sideBar={false}
                    enableRangeSelection={true}
                    suppressExcelExport={true}
                    suppressRowClickSelection={true}
                    pivotPanelShow={'never'}
                    rowGroupPanelShow={'never'}
                    popupParent={popupParent}
                    sortingOrder={sortingOrder}
                    onFirstDataRendered={onFirstDataRendered}
                    alwaysShowVerticalScroll={true}
                    gridOptions={gridOptions}
                    onFilterChanged={handleFilterChanged}
                    onSelectionChanged={onSelectionChanged}
                    onSortChanged={handleSortChanged}
                    onGridReady={handleGridReady}
                    onColumnMoved={handleColumnChanged}
                    onColumnVisible={handleColumnChanged}
                  />
                  {gridParams && (
                    <FilterHandler
                      params={gridParams}
                      gridData={gridData}
                      columnDefs={columnDefs}
                      filterDefaultSetRef={filterDefaultSetRef}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation (Bootstrap #delete modal — same as the other list modules) */}
      <div id="dropdown_modal">
        <div className="modal fade" id="delete" tabIndex="-1" aria-labelledby="modalLabel" aria-hidden="true">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="warning-content">
                <div className="warningOuter">
                  <div className="alertImg">
                    <img src="../assets/images/delete-record.png" alt="Delete Record"></img>
                  </div>
                  <div className="warningText">Are you sure you want to delete the selected record?</div>
                </div>
                <div className="notes">*Note: Any associated Activities, Visits, Drafts will also be Deleted.</div>
                <div className="warningBtn mt-2">
                  <Button variant="secondary" className="btn" data-bs-dismiss="modal" id="massDeleteModal">
                    Cancel
                  </Button>
                  <Button className="btn" variant="danger" onClick={handleClickDelete}>
                    Confirm Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Tasks;
